import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as S from './LogTable.styled';
import { useLogs, useLogsSelector } from '../hooks';
import type { Log } from '../hooks';
import { convertDate } from 'utils';
import './styles.css';
import TablePaginator from 'pages/components/TablePaginator/TablePaginator';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  ModalParts,
  selectCurrency,
  selectOrganizationData,
} from 'pages/redux';
import {
  activityLogsBoundSelectionHint,
  activityLogsCheckedToRevealLog,
  activityLogsTableTypeName,
  activityLogsTypeAttributeControlVisible,
  activityLogsTypeAttributeOptionKeys,
  normalizeActivityLogsTypeAttributeMode,
  CASH_SESSIONS_EMPTY_ONLY_LOG_ID,
  type ActivityLogsTypeAttributeMode,
} from '../logUtils';
import ActivityLogsHeaderMenu from './ActivityLogsHeaderMenu';
import ActivityLogsValueActionsMenu, { COMBINE_SKU_ACTIVITY_LOG_LOCK_INFO } from './ActivityLogsValueActionsMenu';
import { formatActivityLogsActivityLogLockLabel, startEndLogIdsFromClicked } from '../activityLogIdRange';
import { useActivityLogsLogIdRange } from '../useActivityLogIdRange';
import { isCatalogCostPriceLog } from '../catalogCostPriceLog';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import QrCode2Outlined from '@mui/icons-material/QrCode2Outlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import NumbersOutlined from '@mui/icons-material/NumbersOutlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import {
  selectActivityLogsTypeAttributeMode,
  selectLogSearch,
  selectLogSearches,
  selectLogSearchOptions,
  selectLogsChecked,
  selectLogsValueMode,
  selectRecentLog,
  selectSingleLogTime,
  selectAssociateElevatedActivityLogs,
  selectActivityLogsViewAs,
  selectActivityLogsOnlyLogIds,
  selectActivityLogsStartLogId,
  selectActivityLogsEndLogId,
  selectIsComplianceActivity,
  selectComplianceActivityStaffId,
  selectComplianceActivityUsername,
  setAssociateElevatedActivityLogs,
  setActivityLogsTypeAttributeMode,
  setComplianceActivity,
  setLogsChecked,
  setLogsValueMode,
  setRecentLog,
  setSingleLogTime,
  setActivityLogsViewAs,
  setActivityLogsOnlyLogIds,
  setActivityLogsStartEndLogIds,
  selectActivityLogsCombineSku,
  selectIsActivityLogsReportLogsFocus,
  ACTIVITY_LOGS_REPORT_LOGS_FOCUS_FILTERS_TOOLTIP,
  selectActivityLogsAllItems,
  selectActivityLogsCashIn,
  selectActivityLogsCashOut,
  selectActivityLogsUseTimeRange,
  selectActivityLogsSearchHasMore,
  selectActivityLogsSearchPage,
  selectActivityLogsSearchLoadedPageCount,
  setActivityLogsCashFilters,
  setLogs,
  setActivityLogsSearchHasMore,
  setActivityLogsSearchPage,
  resetActivityLogsSearchPaginator,
  setActivityLogsSearchQueued,
  editLog,
} from '../redux';
import DeleteLog from '../modals/DeleteLog';
import RestoreLog from '../modals/RestoreLog';
import CloseReviewedReport from '../modals/CloseReviewedReport';
import PreviewDeletedLog from '../modals/PreviewDeletedLog';
import LockOutlined from '@mui/icons-material/LockOutlined';
import LockOpenOutlined from '@mui/icons-material/LockOpenOutlined';
import SearchOff from '@mui/icons-material/SearchOff';
import ReceiptLong from '@mui/icons-material/ReceiptLong';
import Checklist from '@mui/icons-material/Checklist';
import RestoreFromTrash from '@mui/icons-material/RestoreFromTrash';
import HighlightOff from '@mui/icons-material/HighlightOff';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import ModalAction from 'pages/components/Modal/views/ModalAction';
import { toast } from 'utils/toast';
import { selectPermission, selectStaffId, selectUsername } from 'redux/global';
import { useSocketEmit } from 'pages/Navigation/hooks';
import { useSettings } from 'pages/Settings/hooks';
import {
  PERM,
  canModifyAllActivityLogs,
  canUseActivityTools,
  isAssociateManager,
  selectCustomers,
} from 'pages/Settings/redux';
import {
  ACTIVITY_LOGS_PER_PAGE,
  activityLogsTablePageCount,
  paginateActivityLogsTable,
  sumActivityLogsTableAmount,
} from '../activityLogsTableAmount';
import {
  hasActiveLogSearch,
} from '../logSearch';
import {
  buildStaffCodeByActorToken,
  buildStaffMaps,
  formatLogActor,
  getLogActorIdentity,
  logMentionsUser,
  resolveActorUser,
} from '../logActor';
import { buildCustomerMaps } from '../logCustomer';
import { wrapLogActivity, wrapLogBold, wrapLogPrice } from '../logFormat';
import {
  applyActivityLogsCombineSku,
  ActivityLogsDisplayLog,
  buildActivityLogsDisplayLogs,
  buildPermissionByActor,
  combinedSkuDateBounds,
  formatCombinedSkuDateSpan,
  formatCombinedSkuDetailsPreview,
  getLogUnits,
  hasLogUnits,
} from '../activityLogsDisplay';
import { formatActivityMessageEntry } from '../formatActivityLogMessageEntry';
import { formatRecordLogLabel, recordStaffCodeFromFields } from 'pages/Records/recordLogLabel';
import { CombinedSkuDetailsText } from '../CombinedSkuDetailsText';
import LogPhotoLargePanel from './LogPhotoLargePanel';
import FiledReportLogPreview, { canPreviewFiledReportLog } from './FiledReportLogPreview';
import { isReportContextStandingLogType } from 'pages/Reporting/reportContextUsers';
import { canCloseFiledReportStandingLog } from 'pages/Reporting/reportRecipientAccess';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import { getCachedIdDocFace } from 'utils/idDocFace';
import { isActivityOccurrenceKey, SHOW_ALL_ACTIVITY_LOGS, boActionLeafId } from '../activityGroupsModel';
import { isLogDeletedType, LOG_TYPE_CHANGE } from '../activityLogLabels';
import { LoaderCard } from 'pages/components/Loader/Loader';
import { persistSharedActivityLogsPage } from 'utils/activityLogsMergedPool';
import { selectErpRecords } from 'pages/Records/redux';

const GCS_IMAGE_BASE = 'https://storage.googleapis.com/images_wiegfbiwudjdvh/';

/** Tree selection stores path keys — still show Value column + totals. */
const checkedShowsCashColumns = (
  tokens: string[],
  groupTypeIdSet: Set<string>,
  skuIdSet: Set<string>,
): boolean => (
  tokens.includes('Show all')
  || tokens.some((id) => (
    groupTypeIdSet.has(id)
    || skuIdSet.has(id)
    || isActivityOccurrenceKey(id)
  ))
);
type ValueMode = 'value' | 'units';

const resolveImageSrc = (img?: string | null) => {
  if (!img) return '';
  if (img.startsWith('data:') || img.startsWith('http')) return img;
  return `${GCS_IMAGE_BASE}${img}`;
};

const LogTable = (
  {
    checked, triggerAllItems, triggerCashIn, triggerCashOut, setModal, setRightBar, setIsLoading, isLoading
  }
  :
  {
    checked: string[],
    triggerAllItems: boolean,
    triggerCashIn: boolean,
    triggerCashOut: boolean,
    // eslint-disable-next-line no-unused-vars
    setModal: (m: ModalParts | null) => void,
    // eslint-disable-next-line no-unused-vars
    setRightBar?: (m: ModalParts | null) => void,
    setIsLoading: Function,
    isLoading: boolean
  }
) => {
  const recentLog = useAppSelector(selectRecentLog);
  const currency = useAppSelector(selectCurrency);
  const organizationData = useAppSelector(selectOrganizationData);
  const logsValueMode = useAppSelector(selectLogsValueMode);
  const activityLogsTypeAttributeMode = useAppSelector(selectActivityLogsTypeAttributeMode);
  const [valueMode, setValueMode] = useState<ValueMode>(logsValueMode || 'value');
  const isUnitsMode = valueMode === 'units';
  const singleLogTime = useAppSelector(selectSingleLogTime);
  const associateElevatedActivityLogs = useAppSelector(selectAssociateElevatedActivityLogs);
  const isComplianceActivityFlag = useAppSelector(selectIsComplianceActivity);
  const complianceActivityStaffId = useAppSelector(selectComplianceActivityStaffId);
  const complianceActivityUsername = useAppSelector(selectComplianceActivityUsername);
  const activityLogsStartLogId = useAppSelector(selectActivityLogsStartLogId);
  const activityLogsEndLogId = useAppSelector(selectActivityLogsEndLogId);
  // Sessions jump primes singleLogTime before mount — start filtered to avoid a full-table flash.
  const [isUseMetricTime, setIsUseMetricTime] = useState<boolean>(() => Boolean(
    singleLogTime?.startDate && singleLogTime?.endDate,
  ));
  const allItems = useAppSelector(selectActivityLogsAllItems);
  const cashIn = useAppSelector(selectActivityLogsCashIn);
  const cashOut = useAppSelector(selectActivityLogsCashOut);
  const activityLogsUseTimeRange = useAppSelector(selectActivityLogsUseTimeRange);
  const dispatch = useAppDispatch();
  const setAllItems = (next: boolean) => {
    dispatch(setActivityLogsCashFilters({ allItems: next }));
  };
  const setCashIn = (next: boolean) => {
    dispatch(setActivityLogsCashFilters({ cashIn: next }));
  };
  const setCashOut = (next: boolean) => {
    dispatch(setActivityLogsCashFilters({ cashOut: next }));
  };
  // Time-off in this table must clear Redux too — Reporting reads singleLogTime,
  // not the local toggle, so a leftover range would pin fewer logs than /logs.
  const stopUsingActivityLogsTimeRange = () => {
    setIsUseMetricTime(false);
    dispatch(setSingleLogTime(null));
  };
  const [isShowDeleteLog, setIsShowDeleteLog] = useState<boolean>(false);
  const [isShowEditSave, setIsShowEditSave] = useState<boolean>(false);
  const page = useAppSelector(selectActivityLogsSearchPage);
  const loadedPageCount = useAppSelector(selectActivityLogsSearchLoadedPageCount);
  const setPage = (next: number) => {
    dispatch(setActivityLogsSearchPage(next));
  };
  const resetPaginator = () => {
    dispatch(resetActivityLogsSearchPaginator());
  };
  // Show-all / cash-chip effects must not run on remount (they would change
  // filters and snap the paginator back to page 1).
  const skipActivityLogsTableMountEffectsRef = useRef(true);
  const [isFetchingPage, setIsFetchingPage] = useState(false);
  const activityLogsSearchHasMore = useAppSelector(selectActivityLogsSearchHasMore);
  // Page rows already fetched this search — skip the BE when walking back.
  const activityLogsPageCacheRef = useRef<Map<number, { logs: Log[], hasMore: boolean }>>(
    new Map(),
  );
  const [highlightLog, setHighlightLog] = useState<number>(-1);
  /** Row id to keep highlighted after jumping from an AUD link (cleared after 10s). */
  const [focusLogId, setFocusLogId] = useState<string>('');
  const focusClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Activity deleted row whose Restore action is toggled open via the list icon. */
  const [restoreOpenId, setRestoreOpenId] = useState<string>('');
  /** User-alert row whose Close report action is toggled open via the same list icon. */
  const [closeReportOpenId, setCloseReportOpenId] = useState<string>('');
  /** Report filed row — full-screen set preview open for this log id. */
  const [filedReportPreviewOpenId, setFiledReportPreviewOpenId] = useState<string>('');
  const [editAmount, setEditAmount] = useState<string>('');
  const [editAmountError, setEditAmountError] = useState<string>('');
  /** Row whose value-column menu is in the edit-amount panel. */
  const [editingLogId, setEditingLogId] = useState<string>('');
  const username = useAppSelector(selectUsername);
  const staffId = useAppSelector(selectStaffId);
  const logsChecked = useAppSelector(selectLogsChecked);
  const permission = useAppSelector(selectPermission);
  const activityLogsViewAs = useAppSelector(selectActivityLogsViewAs);
  const activityLogsOnlyLogIds = useAppSelector(selectActivityLogsOnlyLogIds);
  const activityLogsCombineSku = useAppSelector(selectActivityLogsCombineSku);
  const isReportLogsFocus = useAppSelector(selectIsActivityLogsReportLogsFocus);
  const viewAsIsCurrentUser = Boolean(
    activityLogsViewAs
    && (
      (staffId && activityLogsViewAs.staffId && String(activityLogsViewAs.staffId) === String(staffId))
      || (
        username
        && activityLogsViewAs.username
        && String(activityLogsViewAs.username).trim().toLowerCase()
          === String(username).trim().toLowerCase()
      )
    ),
  );
  const useViewAsSender = Boolean(activityLogsViewAs) && !viewAsIsCurrentUser;
  // Shared report: use the author's access so a lower-perm recipient sees the same logs.
  const viewPermission = useViewAsSender
    ? (Number(activityLogsViewAs!.permission) || 0)
    : (Number(permission) || 0);
  const viewStaffId = useViewAsSender ? activityLogsViewAs!.staffId : staffId;
  const viewUsername = useViewAsSender ? activityLogsViewAs!.username : username;
  const logSearch = useAppSelector(selectLogSearch);
  const logSearchOptions = useAppSelector(selectLogSearchOptions);
  const logSearches = useAppSelector(selectLogSearches);
  const { logs, updateLocalLog, logGroups, activityGroupsModel, deletedDepartmentNames, deletedInventoryItemNames, deletedInventoryGroupNames, runActivityLogsSearch } = useLogs();
  const { onClickShowAll } = useLogsSelector({
    triggerAllItems0: () => {
      setAllItems(true);
      setCashIn(true);
      setCashOut(true);
      stopUsingActivityLogsTimeRange();
    },
  });
  const {
    dateById: logIdDateById,
    applyLogIdRangeBound,
    clearLogIdRangeBound,
    clearLogIdRange,
  } = useActivityLogsLogIdRange(logs);
  const { users } = useSettings();
  const customers = useAppSelector(selectCustomers);
  const headers = logs?.length ? ['type', 'message', 'date', 'value'] : [];
  const { action }: any = useParams();
  const socketEmit = useSocketEmit();

  const staffMaps = useMemo(() => buildStaffMaps(users), [users]);
  const erpRecords = useAppSelector(selectErpRecords);
  const staffCodeByToken = useMemo(
    () => buildStaffCodeByActorToken(users, erpRecords || []),
    [users, erpRecords],
  );
  // Legacy change lines stored bare erp UUIDs — resolve to Name(CODE) when rendering.
  const erpLabelById = useMemo(() => {
    const map = new Map<string, string>();
    (erpRecords || []).forEach((row) => {
      const id = String(row?.id || '').trim();
      if (!id) return;
      map.set(
        id,
        formatRecordLogLabel(
          row.name,
          recordStaffCodeFromFields(String(row.kind || ''), row.fields),
          id,
        ),
      );
    });
    return map;
  }, [erpRecords]);
  const customerMaps = useMemo(() => buildCustomerMaps(customers), [customers]);

  const userPhotoByName = useMemo(() => {
    const map = new Map<string, string>();
    (users || []).forEach((u) => {
      const src = resolveImageSrc(u.photo);
      if (!src || !u.username) return;
      map.set(u.username.replace(/\s+/g, '').toLowerCase(), src);
      if (u.id != null && String(u.id) !== '') {
        map.set(String(u.id), src);
      }
    });
    return map;
  }, [users]);

  /** Instant face crops only — never kick off MediaPipe from the activityLogs table. */
  const customerPhotoByToken = useMemo(() => {
    const map = new Map<string, string>();
    (customers || []).forEach((c) => {
      // Prefer seeded / stored face photo (images bucket) over in-memory MediaPipe cache.
      const seeded = resolveImageSrc(c.facePhoto);
      const src = seeded || getCachedIdDocFace(c.idDocUrl);
      if (!src) return;
      if (c.id != null && String(c.id) !== '') {
        map.set(String(c.id), src);
      }
      if (c.name) {
        map.set(c.name.replace(/\s+/g, '').toLowerCase(), src);
      }
    });
    return map;
  }, [customers]);

  const logsMax = ACTIVITY_LOGS_PER_PAGE;

  const { startDate, endDate } = (singleLogTime || {});

  const permissionByActor = useMemo(() => buildPermissionByActor(users || []), [users]);

  /** Compliance review unlock: Associate Managers may see higher-role activityLogs until range changes/off. */
  const complianceReviewActive = Boolean(
    associateElevatedActivityLogs
    && isUseMetricTime
    && startDate
    && endDate,
  );
  /**
   * Compliance → associated activityLogs: after the compliance cashier's first
   * department units sale, hide other users. Off when time range is toggled
   * off or changed.
   */
  const isComplianceActivity = Boolean(
    isComplianceActivityFlag
    && isUseMetricTime
    && startDate
    && endDate
    && complianceActivityStaffId,
  );
  const complianceReviewAllActors = Boolean(
    isAssociateManager(permission) && complianceReviewActive,
  );

  const sessionActor = useMemo(() => ({ staffId, username }), [staffId, username]);

  const actorPermission = (log: {
    message: string;
    created_by?: string | null;
  }) => {
    const actor = getLogActorIdentity(log);
    if (!actor) return null;
    const resolved = resolveActorUser(actor, staffMaps);
    return permissionByActor.get(String(actor))
      ?? (resolved?.id != null ? permissionByActor.get(String(resolved.id)) : undefined)
      ?? (resolved?.username
        ? permissionByActor.get(resolved.username.replace(/\s+/g, '').toLowerCase())
        : undefined)
      ?? null;
  };

  /**
   * Compliance → associated activityLogs: bare higher-role activityLogs (no data.units) stay hidden
   * for every viewer, including Manager+ / the author of the log.
   */
  const isComplianceBlockedBareHighPermLog = (log: {
    message: string;
    created_by?: string | null;
    units?: string | number | null;
    data?: { units?: string | number | null };
  }) => {
    if (!complianceReviewActive) return false;
    if (hasLogUnits(log)) return false;
    const actorPerm = actorPermission(log);
    if (actorPerm != null && actorPerm <= PERM.SUB_MANAGER) return false;
    return true;
  };

  /** Peer-level modify gate — unrestricted view types stay non-editable above peer. */
  const canAssociateModifyLog = (log: {
    type?: string;
    message: string;
    created_by?: string | null;
    units?: string | number | null;
    data?: { units?: string | number | null };
  }) => {
    if (isComplianceBlockedBareHighPermLog(log)) return false;
    const actor = getLogActorIdentity(log);
    if (!actor) return false;
    if (logMentionsUser(log.message, sessionActor)
      || (staffId && String(log.created_by || '') === String(staffId))) {
      return true;
    }
    const actorPerm = actorPermission(log);
    if (actorPerm == null) return false;
    if (actorPerm <= PERM.SUB_MANAGER) return true;
    return Boolean(complianceReviewAllActors && hasLogUnits(log));
  };

  const hasActiveSearch = hasActiveLogSearch(logSearches, logSearch);
  // Logs exist but nothing is checked — prompt to pick types, not a failed search.
  const needsActivityType = Boolean(logs?.length) && !hasActiveSearch && !checked.length;

  const searchArchives = useMemo(() => ({
    deletedTypes: deletedInventoryItemNames,
    deletedGroups: deletedInventoryGroupNames,
    deletedDepartments: deletedDepartmentNames,
  }), [deletedInventoryItemNames, deletedInventoryGroupNames, deletedDepartmentNames]);

  const departmentNames = useMemo(
    () => new Set(logGroups.Departments || []),
    [logGroups.Departments],
  );

  const sharedDisplayParams = {
    logs: logs || [],
    permission: viewPermission,
    staffId: viewStaffId,
    username: viewUsername,
    users: users || [],
    erpRecords: erpRecords || [],
    logSearches: logSearches || [],
    logSearch: logSearch || '',
    logSearchOptions,
    staffMaps,
    customerMaps,
    departmentNames,
    searchArchives,
    logGroups: logGroups as Record<string, string[]>,
    activityGroupsModel,
    startDate,
    endDate,
    valueMode: (isUnitsMode ? 'units' : 'value') as 'value' | 'units',
    associateElevatedActivityLogs: Boolean(associateElevatedActivityLogs),
    isComplianceActivity,
    complianceActivityStaffId,
    onlyLogIds: activityLogsOnlyLogIds,
    startLogId: activityLogsStartLogId,
    endLogId: activityLogsEndLogId,
  };

  // Permission + search + sort + type checks (no time/cash) — recent-log jump helper.
  const getSelectLogs = (checked1: string[]) => buildActivityLogsDisplayLogs({
    ...sharedDisplayParams,
    checked: checked1,
    useTimeRange: false,
    allItems: true,
    cashIn: true,
    cashOut: true,
    associateElevatedActivityLogs: false,
    isComplianceActivity: false,
  });

  // Same pipeline as Reporting (useActivityLogsDisplayLogs → buildActivityLogsDisplayLogs).
  const rawDisplayLogs = buildActivityLogsDisplayLogs({
    ...sharedDisplayParams,
    checked,
    useTimeRange: isUseMetricTime,
    allItems,
    cashIn,
    cashOut,
  });
  const allDisplayLogs: ActivityLogsDisplayLog[] = useMemo(() => {
    if (!activityLogsCombineSku) return rawDisplayLogs;
    return applyActivityLogsCombineSku(
      rawDisplayLogs,
      (log) => activityLogsTableTypeName(
        log,
        checked,
        logGroups,
        activityLogsTypeAttributeMode,
        activityGroupsModel,
      ),
      isUnitsMode ? 'units' : 'value',
    );
  }, [
    rawDisplayLogs,
    activityLogsCombineSku,
    isUnitsMode,
    checked,
    logGroups,
    activityLogsTypeAttributeMode,
    activityGroupsModel,
  ]);
  // /logs table only mounts after search — each paginator page is one searchlogs fetch.
  const fetchLogsFromDb = true;
  const displayLogs = fetchLogsFromDb
    ? allDisplayLogs
    : paginateActivityLogsTable(allDisplayLogs, page, logsMax);
  // Short table = this search is already complete locally — do not hit the BE for “next”.
  const pageIsShort = displayLogs.length < logsMax;
  const canFetchNextDbPage = fetchLogsFromDb && activityLogsSearchHasMore && !pageIsShort;
  const pagesCount = fetchLogsFromDb
    ? Math.max(1, canFetchNextDbPage ? page + 1 : page)
    : activityLogsTablePageCount(allDisplayLogs.length, logsMax);

  const persistActivityLogsPageCache = (
    pageNum: number,
    pageLogs: Log[],
    hasMore: boolean,
  ) => {
    activityLogsPageCacheRef.current.set(pageNum, { logs: pageLogs, hasMore });
    const orgId = organizationData?.id;
    if (orgId == null || orgId === '') return;
    persistSharedActivityLogsPage(String(orgId), pageNum, { logs: pageLogs, hasMore });
  };

  const applyCachedActivityLogsPage = (pageNum: number) => {
    const cached = activityLogsPageCacheRef.current.get(pageNum);
    if (!cached?.logs.length) return false;
    dispatch(setLogs(cached.logs));
    dispatch(setActivityLogsSearchHasMore(cached.hasMore));
    setPage(pageNum);
    return true;
  };

  const handleActivityLogsPageChange = async (nextPage: number) => {
    if (isFetchingPage || nextPage === page || nextPage < 1) return;
    if (fetchLogsFromDb) {
      if (nextPage > page && pageIsShort) return;
      if (applyCachedActivityLogsPage(nextPage)) return;
      setIsFetchingPage(true);
      try {
        const res = await runActivityLogsSearch('search', nextPage, { silent: true });
        const body = res?.data && !Array.isArray(res.data)
          ? res.data as { logs?: Log[], hasMore?: boolean }
          : { logs: (res?.data || []) as Log[], hasMore: false };
        const rows = body.logs || [];
        if (res?.status !== 'success' || rows.length === 0) {
          return;
        }
        persistActivityLogsPageCache(nextPage, rows, Boolean(body.hasMore));
        setPage(nextPage);
      } finally {
        setIsFetchingPage(false);
      }
      return;
    }
    setPage(nextPage);
  };

  // Keep the current page's rows (not always page 1) so return visits stay on page 2+.
  useEffect(() => {
    if (!logs?.length || activityLogsPageCacheRef.current.has(page)) return;
    persistActivityLogsPageCache(page, logs, activityLogsSearchHasMore);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only reset when filters actually change after this table is on screen.
  const checkedKey = checked.join('\0');
  const activityLogsPaginatorFilterKey = [
    String(cashIn),
    String(cashOut),
    String(allItems),
    valueMode,
    String(activityLogsCombineSku),
    checkedKey,
  ].join('\0');
  const activityLogsPaginatorFilterKeyRef = useRef(activityLogsPaginatorFilterKey);
  useEffect(() => {
    if (activityLogsPaginatorFilterKeyRef.current === activityLogsPaginatorFilterKey) return;
    activityLogsPaginatorFilterKeyRef.current = activityLogsPaginatorFilterKey;
    if (!recentLog) resetPaginator();
  }, [activityLogsPaginatorFilterKey, recentLog]);

  useEffect(() => {
    if (valueMode === logsValueMode) return;
    setValueMode(logsValueMode);
    // Compliance units jump: hide N/A rows by default (Units-only on).
    setAllItems(!(associateElevatedActivityLogs && logsValueMode === 'units'));
    if (logsValueMode === 'value') {
      setCashIn(true);
      setCashOut(true);
    }
  }, [logsValueMode, valueMode, associateElevatedActivityLogs]);

  // Compliance → view sales units activityLogs: start with Units-only on so bare directs stay hidden.
  useEffect(() => {
    if (associateElevatedActivityLogs && logsValueMode === 'units') {
      setAllItems(false);
    }
  }, [associateElevatedActivityLogs, logsValueMode]);

  useEffect(() => {
    if (recentLog) {
      stopUsingActivityLogsTimeRange();
      setAllItems(true);
      setCashIn(true);
      setCashOut(true);
      const thisLog = (logs || []).find(
        (l) => l.id.toString() === recentLog.toString()
      );
      if (thisLog) {
        const newChecked = activityLogsCheckedToRevealLog(checked, thisLog, activityGroupsModel);
        const thisIndex = getSelectLogs(newChecked).findIndex((l) => l.id.toString() === recentLog
          .toString());
        if (thisIndex > -1) {
          const thisPage = Math.ceil(Number(thisIndex) / logsMax);
          dispatch(setLogsChecked(newChecked));
          setPage(thisPage < 1 ? 1 : thisPage);
        }
      }
    }
  }, [recentLog, logs]);

  useEffect(() => {
    if (skipActivityLogsTableMountEffectsRef.current) return;
    stopUsingActivityLogsTimeRange();
    setAllItems(true);
    setCashIn(true);
    setCashOut(true);
  }, [triggerAllItems]);

  useEffect(() => {
    if (skipActivityLogsTableMountEffectsRef.current) return;
    stopUsingActivityLogsTimeRange();
    setCashIn(true);
  }, [triggerCashIn]);

  useEffect(() => {
    if (skipActivityLogsTableMountEffectsRef.current) return;
    stopUsingActivityLogsTimeRange();
    setCashOut(true);
  }, [triggerCashOut]);

  useEffect(() => {
    dispatch(setRecentLog(''));
    if (!action) return undefined;
    const action1 = action.replace('svg1ere', '').replace('xxyv', '');
    setIsUseMetricTime(true);
    if (Number(action1) === 0) {
      return undefined;
    }
    setAllItems(false);
    if (Number(action1) === 1) {
      setCashOut(false);
    }
    if (Number(action1) === 2) {
      setCashIn(false);
    }
    if (Number(action1) === 4) {
      setAllItems(true);
    }
    return undefined;
  }, []);

  // Keep Time on when Sessions (or other jumps) re-prime singleLogTime.
  useEffect(() => {
    if (singleLogTime?.startDate && singleLogTime?.endDate) {
      setIsUseMetricTime(true);
      return;
    }
    setIsUseMetricTime(false);
  }, [singleLogTime]);

  // Local Time-off (e.g. Show all) used to leave singleLogTime set, so Reporting
  // still filtered by that range. Drop it whenever the table is not using Time.
  useEffect(() => {
    // Time can be turned on from the left pane; don't wipe a just-applied range.
    if (isUseMetricTime || activityLogsUseTimeRange) return;
    if (!singleLogTime?.startDate) return;
    dispatch(setSingleLogTime(null));
  }, [isUseMetricTime, activityLogsUseTimeRange, singleLogTime, dispatch]);

  const logIdRangeActive = Boolean(activityLogsStartLogId || activityLogsEndLogId);
  const [activityLogLockChipVisible, setActivityLogLockChipVisible] = useState(logIdRangeActive);
  const [activityLogLockChipLeaving, setActivityLogLockChipLeaving] = useState(false);

  useEffect(() => {
    if (logIdRangeActive) {
      setActivityLogLockChipLeaving(false);
      setActivityLogLockChipVisible(true);
      return undefined;
    }
    if (!activityLogLockChipVisible) return undefined;
    setActivityLogLockChipLeaving(true);
    const hideTimer = window.setTimeout(() => {
      setActivityLogLockChipVisible(false);
      setActivityLogLockChipLeaving(false);
    }, 220);
    return () => window.clearTimeout(hideTimer);
  }, [logIdRangeActive, activityLogLockChipVisible]);

  const activityLogLockLabel = formatActivityLogsActivityLogLockLabel(
    activityLogsStartLogId,
    activityLogsEndLogId,
  );
  const lastActivityLogLockLabelRef = useRef(activityLogLockLabel);
  if (activityLogLockLabel) lastActivityLogLockLabelRef.current = activityLogLockLabel;
  const activityLogLockChipLabel = activityLogLockLabel
    || lastActivityLogLockLabelRef.current
    || 'Activity log';

  const unlockActivityLogLock = () => {
    clearLogIdRange();
    setPage(1);
  };

  const sourceIdsForLogRow = (log: { id?: string, combinedSourceIds?: string[] }) => (
    log.combinedSourceIds?.length
      ? log.combinedSourceIds.map((id) => String(id))
      : [String(log.id || '')]
  );

  const logIdBoundsForRow = (log: { id?: string, combinedSourceIds?: string[] }) => {
    const sourceIds = sourceIdsForLogRow(log);
    const bounds = startEndLogIdsFromClicked(sourceIds, logIdDateById);
    return {
      sourceIds,
      startLogId: bounds.startLogId || String(log.id || ''),
      endLogId: bounds.endLogId || bounds.startLogId || String(log.id || ''),
    };
  };

  /** Row → start/end activity-log slice (does not toggle Time). */
  const applyActivityLogLockBound = (which: 'start' | 'end', sourceIds: string[]) => {
    applyLogIdRangeBound(which, sourceIds);
    setPage(1);
  };

  /** Unset a bound pinned to this row. Remaining bound stays; none left unlocks. */
  const clearActivityLogLockBound = (which: 'start' | 'end') => {
    const prevStart = String(activityLogsStartLogId || '').trim();
    const prevEnd = String(activityLogsEndLogId || '').trim();
    if (!prevStart && !prevEnd) {
      unlockActivityLogLock();
      return;
    }
    if (!prevStart || !prevEnd || prevStart === prevEnd) {
      unlockActivityLogLock();
      return;
    }
    clearLogIdRangeBound(which);
    setPage(1);
  };

  const formatActivityLogLockDateLabel = (d: Date | string) => (
    convertDate(new Date(d)).replace('. 00:00am', '').replace('00:00am', '').trim()
  );

  const rowLockProps = (row: ActivityLogsDisplayLog) => {
    const lockDisabled = activityLogsCombineSku || isReportLogsFocus;
    const lockDisabledReason = isReportLogsFocus
      ? ACTIVITY_LOGS_REPORT_LOGS_FOCUS_FILTERS_TOOLTIP
      : COMBINE_SKU_ACTIVITY_LOG_LOCK_INFO;
    if (row.isCombinedSku) {
      const bounds = combinedSkuDateBounds(row.sourceDates || []);
      const idBounds = logIdBoundsForRow(row);
      return {
        rowStartLogId: idBounds.startLogId,
        rowEndLogId: idBounds.endLogId,
        startLabel: bounds ? formatActivityLogLockDateLabel(bounds.start) : '',
        endLabel: bounds ? formatActivityLogLockDateLabel(bounds.end) : '',
        onSetStart: () => applyActivityLogLockBound('start', idBounds.sourceIds),
        onSetEnd: () => applyActivityLogLockBound('end', idBounds.sourceIds),
        onClearStart: () => clearActivityLogLockBound('start'),
        onClearEnd: () => clearActivityLogLockBound('end'),
        lockDisabled,
        lockDisabledReason,
      };
    }
    const dateLabel = formatActivityLogLockDateLabel(row.date);
    return {
      rowStartLogId: String(row.id || ''),
      rowEndLogId: String(row.id || ''),
      startLabel: dateLabel,
      endLabel: dateLabel,
      onSetStart: () => applyActivityLogLockBound('start', [String(row.id || '')]),
      onSetEnd: () => applyActivityLogLockBound('end', [String(row.id || '')]),
      onClearStart: () => clearActivityLogLockBound('start'),
      onClearEnd: () => clearActivityLogLockBound('end'),
      lockDisabled,
      lockDisabledReason,
    };
  };

  const exitReportLogsFocus = () => {
    setIsUseMetricTime(false);
    dispatch(setSingleLogTime(null));
    // Exit means: drop elevated/compliance and remove any pinned log constraints,
    // then re-run the /logs search so the table is refreshed.
    dispatch(setAssociateElevatedActivityLogs(false));
    dispatch(setComplianceActivity({ active: false }));
    dispatch(setActivityLogsViewAs(null));
    dispatch(setActivityLogsOnlyLogIds(null));
    dispatch(setActivityLogsStartEndLogIds(null));
    dispatch(setActivityLogsSearchQueued(true));
  };

  // Cash sessions pin onlyLogIds (+ elevate) without viewAs — not a staff-report jump.
  const isCashSessionsLogsPin = Boolean(
    activityLogsOnlyLogIds?.length
    && !activityLogsViewAs
    && (
      associateElevatedActivityLogs
      || activityLogsOnlyLogIds[0] === CASH_SESSIONS_EMPTY_ONLY_LOG_ID
    ),
  );
  const reportViewAsBanner = isReportLogsFocus && !isCashSessionsLogsPin ? (
    <S.ReportViewAsBanner>
      <span>
        {activityLogsViewAs && !viewAsIsCurrentUser ? (
          <>
            Viewing logs as
            {' '}
            {activityLogsViewAs.label || activityLogsViewAs.username || 'the other party'}
            {' '}
            (same logs as the staff report). Click exit to view as me.
          </>
        ) : activityLogsOnlyLogIds?.length === 1
          ? 'Showing this log from the report. Click exit to view as me.'
          : activityLogsOnlyLogIds?.length
            ? 'Showing these logs from outer source. Click exit to view as me.'
            : 'Viewing logs as yourself (report filters still apply).'}
      </span>
      <S.ReportViewAsActions>
        <S.ReportViewAsExit type="button" onClick={exitReportLogsFocus}>
          Exit
        </S.ReportViewAsExit>
      </S.ReportViewAsActions>
    </S.ReportViewAsBanner>
  ) : null;

  const cashGroupTypes = [
    ...logGroups.Departments,
    ...(logGroups['Department entries'] || []),
    ...logGroups.Inventory,
    ...logGroups['Inventory Group'],
  ];
  // Dynamic groups: Show all / groupType / SKU / tree path selection shows cash columns.
  const isCashGroup = Boolean(
    activityGroupsModel?.groups?.length
      ? checkedShowsCashColumns(
        checked,
        activityGroupsModel.groupTypeIdSet,
        activityGroupsModel.skuIdSet,
      )
      : cashGroupTypes.filter((g) => !checked.includes(g)).length !== cashGroupTypes.length,
  );
  const boundSelectionHint = activityGroupsModel?.groups?.length
    ? null
    : activityLogsBoundSelectionHint(checked, logGroups);

  const canModifyLogEntry = (log: {
    id?: string | number | null,
    type?: string,
    message: string,
    created_by?: string | null,
    catalogCostPrice?: unknown,
    eventType?: unknown,
    event_type?: unknown,
  }) => {
    // Fetch-only catalog cost overlays — not real activity_logs rows
    if (isCatalogCostPriceLog(log)) return false;
    if (canModifyAllActivityLogs(permission)) return true;
    if (isAssociateManager(permission)) return canAssociateModifyLog(log);
    return false;
  };

  /** Jump to an activity by id: page + center + highlight for 10s. */
  const jumpToActivityId = (targetId: string) => {
    const id = String(targetId);
    const exists = (logs || []).some((l) => l.id.toString() === id);
    if (!exists) {
      toast('Activity log not found', { type: 'error' });
      return;
    }
    setFocusLogId(id);
    if (focusClearTimer.current) clearTimeout(focusClearTimer.current);
    focusClearTimer.current = setTimeout(() => setFocusLogId(''), 10000);
    dispatch(setRecentLog(id));
  };

  const formattedEntry = (
    entry: string,
    opts?: { unitsMode?: boolean, parentLog?: Log },
  ) => formatActivityMessageEntry(entry, {
    currency,
    staffMaps,
    staffCodeByToken,
    erpLabelById,
    customerMaps,
    userPhotoByName,
    customerPhotoByToken,
    organizationId: organizationData?.id,
    unitsMode: opts?.unitsMode,
    parentLog: opts?.parentLog,
    canPreviewDeletedActivity: Boolean(
      opts?.parentLog
      && canModifyLogEntry(opts.parentLog)
      && isLogDeletedType(opts.parentLog.type)
      && opts.parentLog.logTable?.id,
    ),
    onActivityIdClick: (targetId) => jumpToActivityId(targetId),
    onPreviewDeletedActivity: (targetId) => {
      const parentLog = opts?.parentLog;
      if (!parentLog?.logTable) return;
      setModal({
        component: (
          <PreviewDeletedLog
            onExit={() => setModal(null)}
            snapshot={parentLog.logTable as Log}
            activityLogId={targetId}
          />
        ),
        exitOnBgClick: () => setModal(null),
      });
    },
    onLogPhotoClick: setRightBar
      ? (src, label) => {
        setRightBar({
          component: <LogPhotoLargePanel title={label} src={src} />,
          exitOnBgClick: () => setRightBar(null),
        });
      }
      : undefined,
  });

  // Footer total for any role when cash/amount columns are in view.
  const isShowTotal = isCashGroup;

  const sumLogAmount = (items: {
    message: string;
    value?: string | number;
    units?: string | number | null;
    data?: { units?: string | number | null };
    created_by?: string | null;
  }[]) => {
    const amountLogs = (isAssociateManager(permission) && !complianceReviewAllActors)
      ? items.filter((l) => (
        logMentionsUser(l.message, sessionActor)
        || Boolean(staffId && String(l.created_by || '') === String(staffId))
      ))
      : items;
    return sumActivityLogsTableAmount(amountLogs, isUnitsMode ? 'units' : 'value');
  };

  // All filtered rows — page-only sums jump (0 on page 1, 16 on page 10) when
  // early pages are N/A hospitality / later pages hold a handful of cash rows.
  const total = sumLogAmount(allDisplayLogs);

  const onChangeEditing = (value: string) => {
    setEditAmountError('');
    const formatted = value.replaceAll(',', '').replaceAll(currency, '');
    if (formatted[0] === '-') {
      if (
        (formatted.length >= 2 && formatted.slice(1, formatted.length - 1).includes('-')) ||
        (/\D+/.test(formatted.slice(1, formatted.length)))
      ) {
        setEditAmountError('Not a valid entry');
        return;
      }
    } else {
      if (formatted.includes('-') || /\D+/.test(formatted)) {
        setEditAmountError('Not a valid entry');
        return;
      }
    }
    setEditAmount(formatted);
  };

  const editingLog = displayLogs.find((l) => String(l.id) === editingLogId);

  const showEditSave = () => {
    if (!editingLog) return;
    setIsShowEditSave(true);
    setModal({
      component: (
        <ModalAction
          header="Save amount edit"
          item="This activity log"
          info={`Change the value of ${currency}${Number(editingLog.value).toLocaleString()} to 
            ${currency}${Number(editAmount).toLocaleString()}?`}
          onExit={() => {
            setIsShowEditSave(false);
            setModal(null);
          }}
          onAct={() => onSubmitEdit()}
        />
      ),
      exitOnBgClick: () => {
        setIsShowEditSave(false);
        setModal(null);
      }
    });
  };

  const onClickSaveEdit = () => {
    setEditAmountError('');
    if (!editAmount) {
      setEditAmountError('Amount is needed');
      return;
    }
    if (!editingLog || editAmount === editingLog.value) {
      setEditAmountError('No changes made');
      return;
    }
    showEditSave();
  };

  const onSubmitEdit = async () => {
    if (!editingLog) return;
    setIsShowEditSave(false);
    setModal(null);
    setIsLoading(true);

    const logToEdit = {
      editId: editingLog.id,
      addLogMessage: `${wrapLogBold(editingLog.type)} ${wrapLogActivity(editingLog.id)} amount changed from ${wrapLogPrice(editingLog.value)} to ${wrapLogPrice(editAmount)} by ${formatLogActor(staffId, username)}`,
      value: editAmount,
    };

    const res = await dispatch(editLog(logToEdit));

    if (res.status === 'success') {
      const newLogsChecked = Array.from(new Set([
        ...logsChecked.filter((c) => c !== 'Log change'),
        SHOW_ALL_ACTIVITY_LOGS,
        boActionLeafId(LOG_TYPE_CHANGE),
      ]));
      dispatch(setLogsChecked(newLogsChecked));
      const newLog = { ...editingLog, value: editAmount };
      updateLocalLog(newLog);
      socketEmit('update_log', { log: newLog });

      // This waits for DB from editLog() to show it's addLog changes
      await new Promise((resolve) => { setTimeout(resolve, 400); });

      setIsLoading(false);
      setPage(1);
      setEditingLogId('');
      setHighlightLog(-1);
      toast('Successfully edited activity log', { type: 'success' });
      return;
    }

    setIsLoading(false);
    showEditSave();
    toast(res.data, { type: 'error' });
  };

  const renderValueActionsMenu = (row: ActivityLogsDisplayLog, rowIndex: number) => {
    const canEditDelete = Boolean(
      !isUnitsMode
      && !row.isCombinedSku
      && canModifyLogEntry(row)
      && (row.value === '0' || Number(row.value))
      && row.id,
    );
    const lock = rowLockProps(row);
    const rowId = String(row.id || '');
    return (
      <ActivityLogsValueActionsMenu
        {...lock}
        rangeStartLogId={activityLogsStartLogId}
        rangeEndLogId={activityLogsEndLogId}
        rangeActive={logIdRangeActive}
        canEdit={canEditDelete}
        canDelete={canEditDelete}
        onDelete={() => {
          setIsShowDeleteLog(true);
          setHighlightLog(rowIndex);
          setModal({
            component: (
              <DeleteLog
                onExit={(refreshPg?: boolean) => {
                  setHighlightLog(-1);
                  if (refreshPg) setPage(1);
                  setIsShowDeleteLog(false);
                  setModal(null);
                }}
                log={row}
              />
            ),
            exitOnBgClick: () => {
              setIsShowDeleteLog(false);
              setModal(null);
            },
          });
        }}
        currency={currency}
        editAmount={editingLogId === rowId ? editAmount : String(row.value || '')}
        editAmountError={editingLogId === rowId ? editAmountError : ''}
        onChangeEditAmount={onChangeEditing}
        onSaveEdit={onClickSaveEdit}
        isEditing={editingLogId === rowId}
        onStartEdit={() => {
          setHighlightLog(rowIndex);
          setEditAmount(String(row.value || ''));
          setEditAmountError('');
          setEditingLogId(rowId);
        }}
        onCancelEdit={() => {
          if (editingLogId === rowId) setEditingLogId('');
        }}
        holdOpen={isShowEditSave || isShowDeleteLog || isLoading}
        onOpenChange={(nextOpen) => {
          if (nextOpen) setHighlightLog(rowIndex);
          else if (!isShowEditSave && editingLogId !== rowId) {
            setHighlightLog((prev) => (prev === rowIndex ? -1 : prev));
          }
        }}
      />
    );
  };

  let recentLogTimer: any;

  useEffect(() => {
    if (recentLog) {
      clearTimeout(recentLogTimer);
      const thisIndex = displayLogs.findIndex((l) => l.id.toString() === recentLog.toString());
      document.getElementById(`tdIndex${thisIndex}`)?.scrollIntoView(
        { behavior: 'smooth', block: 'center', inline: 'center' }
      );
      recentLogTimer = setTimeout(() => dispatch(setRecentLog('')), 400);
    } else {
      document.getElementById('table_cont')?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [page, cashIn, cashOut, allItems, checked, valueMode]);

  useEffect(() => {
    const hasCashSelection = activityGroupsModel?.groups?.length
      ? checkedShowsCashColumns(
        checked,
        activityGroupsModel.groupTypeIdSet,
        activityGroupsModel.skuIdSet,
      )
      : cashGroupTypes.filter((b) => checked.includes(b)).length > 0;
    if (skipActivityLogsTableMountEffectsRef.current) return;
    if (!hasCashSelection && checked.length) {
      setAllItems(true);
      setCashIn(true);
      setCashOut(true);
    }
  }, [checked, activityGroupsModel]);

  useEffect(() => {
    skipActivityLogsTableMountEffectsRef.current = false;
  }, []);

  useEffect(() => {
    setEditAmount(editingLog?.value || '');
    setEditAmountError('');
  }, [editingLogId]);

  const focusInd = focusLogId
    ? displayLogs.findIndex((l) => l.id.toString() === focusLogId)
    : -1;
  const rowHighlight = focusInd >= 0 ? focusInd : highlightLog;

  // Keep the jumped-to activity centered while the 10s focus highlight is active.
  useEffect(() => {
    if (!focusLogId || focusInd < 0) return undefined;
    const t = window.setTimeout(() => {
      document.getElementById(`tdIndex${focusInd}`)?.scrollIntoView({
        behavior: 'smooth', block: 'center', inline: 'center',
      });
    }, 50);
    return () => window.clearTimeout(t);
  }, [focusLogId, focusInd, page]);

  useEffect(() => () => {
    if (focusClearTimer.current) clearTimeout(focusClearTimer.current);
  }, []);

  const hasTableRows = allDisplayLogs.length > 0;
  const filedReportPreviewLog = filedReportPreviewOpenId
    ? (displayLogs.find((l) => String(l.id) === filedReportPreviewOpenId)
      || (logs || []).find((l) => String(l.id) === filedReportPreviewOpenId)
      || null)
    : null;

  return (
    <S.Container isCashGroup={isShowTotal} $empty={!hasTableRows}>
      {hasTableRows ? (
      <S.AllCont>
        <S.Cont1 id="table_cont">
          <S.Table
            isCashGroup={isCashGroup}
            itemCount={(logsMax * page).toString().length}
          >
            <S.THead>
              <S.TR>
                {
                  headers.length ? (
                    <S.TH7
                      isEmpty={displayLogs.length === 0}
                      itemCount={(logsMax * page).toString().length}
                      isCashGroup={isCashGroup}
                    >
                      S/no
                    </S.TH7>
                  ) : (null)
                }
                {
                  headers.map((hTmp: string) => {
                    let h = JSON.parse(JSON.stringify([...hTmp]));
                    h[0] = h[0].toUpperCase();
                    h = h.join('');
                    if (h === 'Message') {
                      h = 'Description';
                      return (
                        <S.TH2
                          isEmpty={displayLogs.length === 0}
                          key={`logH_${h}`}
                          isCashGroup={isCashGroup}
                        >
                          {h}
                        </S.TH2>
                      );
                    }
                    if (h === 'Value') {
                      if (!isCashGroup) {
                        return null;
                      }
                      return (
                        <S.TH4
                          isEmpty={displayLogs.length === 0}
                          key={`logH_${h}`}
                          isCashGroup={isCashGroup}
                        >
                          <ActivityLogsHeaderMenu
                            ariaLabel="Attribute value column"
                            title="Attribute by value or units"
                            sectionLabel="Attribute by"
                            value={valueMode}
                            lineTrigger
                            flush
                            options={[
                              {
                                value: 'value',
                                label: 'Value',
                                hint: 'Show monetary amounts',
                                icon: <PaymentsOutlined fontSize="inherit" />,
                              },
                              {
                                value: 'units',
                                label: 'Units',
                                hint: 'Show inventory unit counts',
                                icon: <NumbersOutlined fontSize="inherit" />,
                              },
                            ]}
                            onChange={(next) => {
                              const mode = (next === 'units' ? 'units' : 'value') as ValueMode;
                              setValueMode(mode);
                              dispatch(setLogsValueMode(mode));
                              setAllItems(true);
                              if (mode === 'value') {
                                setCashIn(true);
                                setCashOut(true);
                              }
                              setPage(1);
                            }}
                          />
                        </S.TH4>
                      );
                    }
                    if (h === 'Type') {
                      h = 'Action';
                      const showAttribute = activityLogsTypeAttributeControlVisible(
                        checked,
                        logGroups,
                      );
                      const attributeKeys = activityLogsTypeAttributeOptionKeys(
                        checked,
                        logGroups,
                      );
                      const effectiveAttributeMode = normalizeActivityLogsTypeAttributeMode(
                        activityLogsTypeAttributeMode,
                        checked,
                        logGroups,
                      );
                      const attributeOptions = attributeKeys.map((key) => {
                        if (key === 'department') {
                          return {
                            value: 'department',
                            label: 'Department',
                            hint: 'Group rows by department',
                            icon: <AccountTreeOutlined fontSize="inherit" />,
                          };
                        }
                        if (key === 'inventory_group') {
                          return {
                            value: 'inventory_group',
                            label: 'Inventory group',
                            hint: 'Group rows by inventory group',
                            icon: <CategoryOutlined fontSize="inherit" />,
                          };
                        }
                        return {
                          value: 'sku',
                          label: 'SKU',
                          hint: 'Show the actual inventory type on each log',
                          icon: <QrCode2Outlined fontSize="inherit" />,
                        };
                      });
                      return (
                        <S.TH3
                          isEmpty={displayLogs.length === 0}
                          key={`logH_${h}`}
                          isCashGroup={isCashGroup}
                        >
                          <S.ActivityHeaderRow>
                            {h}
                            {showAttribute ? (
                              <ActivityLogsHeaderMenu
                                ariaLabel="Attribute activity types"
                                title="Attribute temporary types"
                                sectionLabel="Attribute by"
                                value={effectiveAttributeMode}
                                lineTrigger
                                trigger={<LayersOutlined fontSize="inherit" />}
                                options={attributeOptions}
                                onChange={(next) => {
                                  dispatch(setActivityLogsTypeAttributeMode(
                                    next as ActivityLogsTypeAttributeMode,
                                  ));
                                }}
                              />
                            ) : null}
                          </S.ActivityHeaderRow>
                        </S.TH3>
                      );
                    }
                    return (
                      <S.TH
                        isEmpty={displayLogs.length === 0}
                        key={`logH_${h}`}
                        isCashGroup={isCashGroup}
                      >
                        {h}
                      </S.TH>
                    );
                  })
                }
              </S.TR>
            </S.THead>
            <tbody style={{ border: 'none', zIndex: '1' }}>
              {
                displayLogs?.map((log, i) => {
                  const isLast = i === displayLogs.length - 1;
                  const isNew = new Date().getTime() - new Date(log.updatedAsOf).getTime() <=
                    (2 * 60 * 1000);
                  const isCombinedSku = log.isCombinedSku === true;
                  const combinedTooltip = isCombinedSku
                    ? `Combined log — ${log.combinedLogCount || 0} value entries rolled up by SKU`
                    : undefined;

                  return (
                    <S.TR2
                      key={`logTR_${isCombinedSku ? `combined_${log.combinedSourceIds?.join('_')}` : log.type}_${i}`}
                      title={combinedTooltip}
                    >
                      <S.TD3
                        isLast={isLast}
                        ind={i}
                        highlighted={rowHighlight}
                        isNew={isNew}
                        id={`tdIndex${i}`}
                      >
                        {i + ((page - 1) * logsMax) + 1}
                      </S.TD3>
                      {
                        headers.map((h) => {
                          let entry: any = (log as any)[h];
                          if (h === 'type') {
                            entry = activityLogsTableTypeName(
                              log,
                              checked,
                              logGroups,
                              activityLogsTypeAttributeMode,
                              activityGroupsModel,
                            );
                            // Compliance standing Action → colored badge (not plain text).
                            const complianceType = S.complianceStandingDisplayLabel(String(entry || ''));
                            if (complianceType) {
                              return (
                                <S.TD
                                  key={`LogTD_${h}_${i}`}
                                  fontWeight={500}
                                  isLast={isLast}
                                  ind={i}
                                  highlighted={rowHighlight}
                                  isNew={isNew}
                                >
                                  <S.ComplianceStandingTypeBadge
                                    $tone={S.statusBadgeToneForText(complianceType)}
                                  >
                                    {complianceType}
                                  </S.ComplianceStandingTypeBadge>
                                </S.TD>
                              );
                            }
                          }
                          if (h === 'date') {
                            if (isCombinedSku) {
                              const bounds = combinedSkuDateBounds(log.sourceDates || []);
                              const dateLabel = formatCombinedSkuDateSpan(log.sourceDates || []);
                              const rangeTitle = bounds
                                ? `${formatActivityLogLockDateLabel(bounds.start)} – ${formatActivityLogLockDateLabel(bounds.end)}`
                                : combinedTooltip;
                              return (
                                <S.TD
                                  key={`LogTD_${h}_${i}`}
                                  isLast={isLast}
                                  ind={i}
                                  highlighted={rowHighlight}
                                  isNew={isNew}
                                >
                                  {isCashGroup ? (
                                    <span title={rangeTitle}>{dateLabel}</span>
                                  ) : (
                                    <S.LockBesideCell title={rangeTitle}>
                                      <span>{dateLabel}</span>
                                      {renderValueActionsMenu(log, i)}
                                    </S.LockBesideCell>
                                  )}
                                </S.TD>
                              );
                            }
                            const dateLabel = formatActivityLogLockDateLabel(entry);
                            return (
                              <S.TD
                                key={`LogTD_${h}_${i}`}
                                isLast={isLast}
                                ind={i}
                                highlighted={rowHighlight}
                                isNew={isNew}
                              >
                                {isCashGroup ? dateLabel : (
                                  <S.LockBesideCell>
                                    <span>{dateLabel}</span>
                                    {renderValueActionsMenu(log, i)}
                                  </S.LockBesideCell>
                                )}
                              </S.TD>
                            );
                          }
                          if (h === 'message' && isCombinedSku) {
                            const sourceActivityTypes = log.sourceMessages?.length
                              ? log.sourceMessages
                              : [String(log.type || '')];
                            const detailsText = formatCombinedSkuDetailsPreview(
                              sourceActivityTypes,
                              log.combinedLogCount || sourceActivityTypes.length,
                              log.uniqueSourceCount,
                            );
                            return (
                              <S.TD1
                                key={`LogTD_${h}_${i}`}
                                isLast={isLast}
                                ind={i}
                                highlighted={rowHighlight}
                                isNew={isNew}
                              >
                                <S.CombinedSkuDetails title={detailsText}>
                                  <CombinedSkuDetailsText text={detailsText} />
                                </S.CombinedSkuDetails>
                              </S.TD1>
                            );
                          }
                          if (h === 'value' && isUnitsMode) {
                            const units = getLogUnits(log);
                            entry = units == null ? 'N/A' : String(units);
                          }

                          entry = formattedEntry(entry, {
                            unitsMode: h === 'value' && isUnitsMode,
                            parentLog: h === 'message' ? log : undefined,
                          });

                          if (h === 'value') {
                            if (!isCashGroup) {
                              return null;
                            }
                            return (
                              <S.TD2
                                key={`LogTD_${h}_${i}`}
                                isLast={isLast}
                                id={`valueTD${i}`}
                                ind={i}
                                highlighted={rowHighlight}
                                isNew={isNew}
                              >
                                {entry[0]}
                                <S.MoreCont>
                                  {renderValueActionsMenu(log, i)}
                                </S.MoreCont>
                              </S.TD2>
                            );
                          }
                          if (h === 'message') {
                            const canRestore = canModifyLogEntry(log)
                              && isLogDeletedType(log.type)
                              && Boolean(log.logTable?.id);
                            const canCloseReviewed = !isCombinedSku
                              && isReportContextStandingLogType(log.type)
                              && Boolean(log.id)
                              && canCloseFiledReportStandingLog(
                                String(log.message || ''),
                                permission,
                                staffMaps,
                              );
                            // Report filed + Compliance standing rows (preview on the log).
                            const canPreviewFiledReport = !isCombinedSku
                              && canPreviewFiledReportLog(log);
                            const restoreOpen = restoreOpenId === String(log.id);
                            const closeOpen = closeReportOpenId === String(log.id);
                            const filedPreviewOpen = filedReportPreviewOpenId === String(log.id);
                            return (
                              <S.TD1
                                key={`LogTD_${h}_${i}`}
                                isLast={isLast}
                                ind={i}
                                highlighted={rowHighlight}
                                isNew={isNew}
                              >
                                {
                                  entry.map((e: any, j: number) => <span key={`tdE_${h}_${i}_${j}`}>{e}</span>)
                                }
                                {canRestore ? (
                                  <S.RestoreActions>
                                    <S.RestoreToggle
                                      type="button"
                                      $open={restoreOpen}
                                      title={restoreOpen ? 'Hide restore' : 'Show restore'}
                                      aria-expanded={restoreOpen}
                                      onClick={() => setRestoreOpenId(
                                        restoreOpen ? '' : String(log.id),
                                      )}
                                    >
                                      <FormatListBulleted sx={{ fontSize: 14 }} />
                                    </S.RestoreToggle>
                                    {restoreOpen ? (
                                      <S.RestoreBtn
                                        title="Restore deleted log"
                                        onClick={() => setModal({
                                          component: (
                                            <RestoreLog
                                              onExit={() => {
                                                setRestoreOpenId('');
                                                setModal(null);
                                              }}
                                              log={log}
                                            />
                                          ),
                                          exitOnBgClick: () => setModal(null),
                                        })}
                                      >
                                        <RestoreFromTrash sx={{ fontSize: 14 }} />
                                        Restore activity
                                      </S.RestoreBtn>
                                    ) : null}
                                  </S.RestoreActions>
                                ) : null}
                                {canCloseReviewed ? (
                                  <S.RestoreActions>
                                    <S.RestoreToggle
                                      type="button"
                                      $open={closeOpen}
                                      title={closeOpen ? 'Hide close' : 'Show close'}
                                      aria-expanded={closeOpen}
                                      onClick={() => setCloseReportOpenId(
                                        closeOpen ? '' : String(log.id),
                                      )}
                                    >
                                      <FormatListBulleted sx={{ fontSize: 14 }} />
                                    </S.RestoreToggle>
                                    {closeOpen ? (
                                      <S.CloseReportBtn
                                        title="Close filed report"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setModal({
                                            component: (
                                              <CloseReviewedReport
                                                onExit={() => {
                                                  setCloseReportOpenId('');
                                                  setModal(null);
                                                }}
                                                activityLogId={String(log.id)}
                                              />
                                            ),
                                            exitOnBgClick: () => setModal(null),
                                          });
                                        }}
                                      >
                                        <HighlightOff sx={{ fontSize: 14 }} />
                                        Close report
                                      </S.CloseReportBtn>
                                    ) : null}
                                  </S.RestoreActions>
                                ) : null}
                                {canPreviewFiledReport ? (
                                  <S.RestoreActions>
                                    <S.RestoreToggle
                                      type="button"
                                      $open={filedPreviewOpen}
                                      title="View filed report"
                                      aria-expanded={filedPreviewOpen}
                                      onClick={() => setFiledReportPreviewOpenId(
                                        filedPreviewOpen ? '' : String(log.id),
                                      )}
                                    >
                                      <FormatListBulleted sx={{ fontSize: 14 }} />
                                    </S.RestoreToggle>
                                    <S.PreviewReportBtn
                                      type="button"
                                      title="View filed report set"
                                      onClick={() => setFiledReportPreviewOpenId(
                                        filedPreviewOpen ? '' : String(log.id),
                                      )}
                                    >
                                      <DescriptionOutlined sx={{ fontSize: 14 }} />
                                      View report
                                    </S.PreviewReportBtn>
                                  </S.RestoreActions>
                                ) : null}
                              </S.TD1>
                            );
                          }
                          return (
                            <S.TD
                              key={`LogTD_${h}_${i}`}
                              fontWeight={h === 'type' ? 500 : undefined}
                              isLast={isLast}
                              ind={i}
                              highlighted={rowHighlight}
                              isNew={isNew}
                            >
                              {
                                entry.map((e: any, j: number) => <span key={`tdE_${h}_${i}_${j}`}>{e}</span>)
                              }
                            </S.TD>
                          );
                        })
                      }
                    </S.TR2>
                  );
                })
              }
            </tbody>
          </S.Table>
        </S.Cont1>
        <S.TFoot isCashGroup={isShowTotal}>
          <S.TotalFooter>
            {reportViewAsBanner}
            <S.PaginationCont>
              <TablePaginator
                page={page}
                pageCount={pagesCount}
                onPageChange={handleActivityLogsPageChange}
                fetchLogsFromDb={fetchLogsFromDb}
                hasMorePages={canFetchNextDbPage}
                loadedPageCount={loadedPageCount}
                disabled={isFetchingPage}
              />
            </S.PaginationCont>
            {
              isShowTotal || activityLogLockChipVisible || isFetchingPage ? (
                <S.TotalCont $withTotal={isShowTotal || isFetchingPage}>
                  {activityLogLockChipVisible ? (
                    <S.ActivityLogLockChip
                      type="button"
                      $leaving={activityLogLockChipLeaving}
                      data-analytics="activityLogs_activity_log_lock_unlock"
                      aria-label="Unlock activity log lock"
                      title={`${activityLogLockChipLabel} locked. Click to unlock.`}
                      onClick={unlockActivityLogLock}
                    >
                      <LockOutlined sx={{ fontSize: 15 }} />
                      <S.ActivityLogLockChipText>
                        {activityLogLockChipLabel}
                        {' '}
                        locked
                      </S.ActivityLogLockChipText>
                      <LockOpenOutlined sx={{ fontSize: 15 }} />
                    </S.ActivityLogLockChip>
                  ) : null}
                  {isFetchingPage ? (
                    <S.TotalCont1>
                      <LoaderCard compact inPage message="Loading…" />
                    </S.TotalCont1>
                  ) : isShowTotal ? (
                    <S.TotalCont1>
                      <S.Total
                        isNegative={!isUnitsMode && (total || 0) < 0}
                        $neutral={isUnitsMode}
                      >
                        {isUnitsMode
                          ? (total || 0).toLocaleString()
                          : `${currency}${(total || 0).toLocaleString()}`}
                      </S.Total>
                    </S.TotalCont1>
                  ) : null}
                </S.TotalCont>
              ) : null
            }
          </S.TotalFooter>
        </S.TFoot>
      </S.AllCont>
      ) : null}
      {
        !hasTableRows ? (
          <S.InfoContainer>
            <S.InfoCont>
              <S.EmptyIconWrapper $prompt={needsActivityType}>
                {hasActiveSearch ? (
                  <SearchOff sx={{ fontSize: 64, opacity: 0.25 }} />
                ) : needsActivityType ? (
                  <Checklist sx={{ fontSize: 64 }} />
                ) : (
                  <ReceiptLong sx={{ fontSize: 64, opacity: 0.25 }} />
                )}
              </S.EmptyIconWrapper>
              <S.NoResults>
                {!logs?.length ? 'No Activity'
                  : hasActiveSearch ? 'No search results'
                    : needsActivityType ? 'Select an activity type'
                      : 'No Results'}
              </S.NoResults>
              <S.Info>
                {!logs?.length
                  ? 'Activity will appear here once it is recorded'
                  : hasActiveSearch
                    ? 'No logs found for your search criteria'
                    : needsActivityType
                      ? 'Choose types from the list to view matching activity'
                      : boundSelectionHint
                        ? boundSelectionHint
                        : (checked.length && isUseMetricTime)
                          ? 'No logs found for the time range'
                          : (checked.length && allItems && !isUseMetricTime && canUseActivityTools(permission))
                            ? 'No logs found for your selection'
                            : (allItems && !canUseActivityTools(permission))
                              ? 'No logs by you'
                              : (checked.length && !allItems && !isUseMetricTime && isUnitsMode)
                                ? 'No unit logs found for your selection'
                                : (checked.length && !allItems && !isUseMetricTime && cashIn && cashOut)
                                  ? 'No cash flow found for your selection'
                                  : (checked.length && !allItems && !isUseMetricTime && cashIn
                                    && !cashOut)
                                    ? 'No cash-inflow found for your selection'
                                    : (checked.length && !allItems && !isUseMetricTime && !cashIn
                                      && cashOut)
                                      ? 'No cash-outflow found for your selection'
                                      : ''}
              </S.Info>
              {needsActivityType ? (
                <S.EmptyShowAllBtn
                  type="button"
                  data-analytics="activityLogs_empty_show_all"
                  onClick={() => onClickShowAll(checked)}
                >
                  Show all
                </S.EmptyShowAllBtn>
              ) : null}
            </S.InfoCont>
          </S.InfoContainer>
        ) : (null)
      }
      {filedReportPreviewLog ? (
        <FiledReportLogPreview
          log={filedReportPreviewLog}
          currency={currency}
          onClose={() => setFiledReportPreviewOpenId('')}
        />
      ) : null}
    </S.Container>
  );
};

export default LogTable;
